/**
 * Round 117 (Phase E3) — Universal Asset Renderer.
 *
 * ONE component that renders ANY builder's output payload with:
 *   - Brand tokens applied as CSS variables (colors, fonts, logos).
 *   - Missing-fields banner.
 *   - "Reused from…" provenance chips per reused section.
 *   - Section list — recursively expands whatever shape the LLM
 *     returned (headings, bullets, quotes, buttons, images).
 *
 * Builders don't ship their own design system. This renderer is
 * the design system.
 */
import React, { useMemo } from 'react';
import { Sparkle } from '@phosphor-icons/react';

const SYSTEM_KEYS = new Set([
  'brand_tokens', 'reused_from', 'missing_fields', 'builder_type',
]);


function renderScalar(value) {
  return (
    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'var(--ua-body-font)' }}>
      {String(value)}
    </p>
  );
}


function renderListItem(item, index) {
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    return (
      <li key={index} className="text-slate-700" style={{ fontFamily: 'var(--ua-body-font)' }}>
        {Object.entries(item).map(function ([k, vv]) {
          const scalar = typeof vv === 'string' || typeof vv === 'number';
          return (
            <div key={k}>
              <span className="font-semibold text-slate-800">{k}: </span>
              <span>{scalar ? String(vv) : JSON.stringify(vv)}</span>
            </div>
          );
        })}
      </li>
    );
  }
  return (
    <li key={index} className="text-slate-700" style={{ fontFamily: 'var(--ua-body-font)' }}>
      {String(item)}
    </li>
  );
}


function renderList(arr) {
  return (
    <ul className="list-disc pl-5 space-y-1">
      {arr.map(renderListItem)}
    </ul>
  );
}


function renderObject(obj) {
  return (
    <div className="space-y-2 pl-2 border-l-2 border-slate-200">
      {Object.entries(obj).map(function ([k, v]) {
        return (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
              {k.replace(/_/g, ' ')}
            </div>
            {renderValue(v)}
          </div>
        );
      })}
    </div>
  );
}


function renderValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number') return renderScalar(value);
  if (Array.isArray(value)) return renderList(value);
  if (typeof value === 'object') return renderObject(value);
  return null;
}


function SectionCard(props) {
  const label = props.name.replace(/_/g, ' ').replace(/\b\w/g, function (c) {
    return c.toUpperCase();
  });
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`ua-section-${props.name}`}>
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-lg font-bold"
          style={{ color: 'var(--ua-primary)', fontFamily: 'var(--ua-heading-font)' }}
        >
          {label}
        </h3>
        {props.reusedInfo && (
          <span
            data-testid={`ua-reused-${props.name}`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-700"
          >
            <Sparkle size={9} weight="fill" /> Reused from {props.reusedInfo.asset_type || 'approved'}
          </span>
        )}
      </div>
      {renderValue(props.value)}
    </section>
  );
}


function UniversalAssetRenderer(props) {
  const payload = props.payload || {};
  const brandTokens = payload.brand_tokens || {};
  const reused = payload.reused_from || {};
  const missing = payload.missing_fields || [];

  const styleVars = useMemo(function () {
    return {
      '--ua-primary': brandTokens.primary || '#0F172A',
      '--ua-primary-alt': brandTokens.primary_alt || '#22C55E',
      '--ua-accent': brandTokens.accent || '#F5F5F4',
      '--ua-heading-font': brandTokens.heading_font
        ? `"${brandTokens.heading_font}", system-ui, sans-serif`
        : 'system-ui, sans-serif',
      '--ua-body-font': brandTokens.body_font
        ? `"${brandTokens.body_font}", system-ui, sans-serif`
        : 'system-ui, sans-serif',
    };
  }, [brandTokens]);

  const sectionEntries = Object.entries(payload).filter(function (kv) {
    return !SYSTEM_KEYS.has(kv[0]);
  });

  return (
    <div className="p-6" style={styleVars} data-testid="universal-asset-renderer">
      <div className="mb-6 flex items-center gap-3">
        {brandTokens.logo_url && (
          <img src={brandTokens.logo_url} alt="" className="h-10 object-contain" />
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Preview</div>
          <div className="text-xl font-bold" style={{ color: 'var(--ua-primary)', fontFamily: 'var(--ua-heading-font)' }}>
            {props.label || 'Generated asset'}
          </div>
          {brandTokens.tagline && (
            <div className="text-sm text-slate-500 italic" style={{ fontFamily: 'var(--ua-body-font)' }}>
              {brandTokens.tagline}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {[brandTokens.primary, brandTokens.primary_alt, brandTokens.accent]
            .filter(Boolean)
            .map(function (c, i) {
              return (
                <span
                  key={i}
                  className="inline-block h-5 w-5 rounded border border-slate-200"
                  style={{ background: c }}
                />
              );
            })}
        </div>
      </div>

      {missing.length > 0 && (
        <div
          data-testid="ua-missing-banner"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 p-3 text-xs"
        >
          <div className="font-semibold mb-1 flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> {missing.length} field{missing.length === 1 ? '' : 's'} used a placeholder
          </div>
          <div className="text-amber-700/90">
            Add these in Settings → Business Brain to strengthen future drafts. Missing: {missing.slice(0, 4).join(', ')}{missing.length > 4 ? '…' : ''}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {sectionEntries.map(function (kv) {
          return (
            <SectionCard
              key={kv[0]}
              name={kv[0]}
              value={kv[1]}
              reusedInfo={reused[kv[0]] || null}
            />
          );
        })}
      </div>
    </div>
  );
}

export default UniversalAssetRenderer;
